import { Controller } from "@hotwired/stimulus";
import { patch } from "@rails/request.js";
import { Current } from "current.js";
import { ethers } from "ethers";

// Connects to data-controller="coinflip"
export default class extends Controller {
  static targets = ["coin", "head", "butt", "amount", "flip", "anim"];

  async connect() {
    const somnia_chain_id = 5031;
    this.is_flipping = false;

    this.provider = new ethers.BrowserProvider(window.ethereum);
    this.signer = await this.provider.getSigner();
    this.flip = await this.#getFlipContract();

    this.coin_index = 0;
    this.coinTarget.hidden = false;

    this.coin_side = null;
    this.bet_amount = null;
    this.buttons = [this.flipTarget, this.headTarget, this.buttTarget, ...this.amountTargets];

    const network = await this.provider.getNetwork();
    this.is_wrong_chain = network.chainId != somnia_chain_id ? true : false;

    if (this.is_wrong_chain) {
      // toast('Wrong blockchain chain detected', { type: 'error', description: 'Please switch chain to Somnia Mainnet' })
      this.#addSomniaNetwork();
      return;
    }
  }

  selectCoinSide(event) {
    if (event.params.name == "head") {
      this.headTarget.setAttribute("data-active", true);
      this.buttTarget.removeAttribute("data-active");
      this.coin_side = "head";
    } else {
      this.buttTarget.setAttribute("data-active", true);
      this.headTarget.removeAttribute("data-active");
      this.coin_side = "butt";
    }
  }

  selectAmount(event) {
    this.amountTargets.forEach((amount) => amount.removeAttribute("data-active"));
    event.currentTarget.toggleAttribute("data-active");

    this.bet_amount = event.currentTarget.dataset.amount;
  }

  async bet() {
    if (this.is_flipping) return;
    if (this.coin_side == null) {
      toast("Coin side hasn't been chosen", { type: "error" });
      return;
    }

    if (this.bet_amount == null) {
      toast("Bet amount hasn't been chosen", { type: "error" });
      return;
    }

    this.#disableButtons();

    // const coin_base_src = this.coinTarget.src.split("/coin/")[0]
    var current_frame = 1;
    var result = null;
    this.is_flipping = true;

    var animation_interval = setInterval(() => {
      if (result === null) {
        current_frame = (current_frame % 12) + 1;
      } else {
        const target = result === "heads" ? 1 : 7;

        if (current_frame === target) {
          clearInterval(animation_interval);
          this.#enableButtons();
          this.#resetButtons();
          this.is_flipping = false;
          return;
        }

        current_frame = (current_frame % 12) + 1;
      }

      this.coinTargets.forEach((coin) => (coin.hidden = true));
      this.coinTargets[current_frame - 1].hidden = false;
    }, 60);

    try {
      const tx = await this.flip.bet(19000, {
        value: ethers.parseEther(this.bet_amount.toString()),
      });
      const receipt = await tx.wait();

      var bet_id = null;

      receipt.logs.forEach((log) => {
        if (log.fragment && log.fragment.name === "Bet") {
          bet_id = log.args[0];
          // console.log("Sender", log.args[1]);
          // console.log("Amount", log.args[2]);
        }
      });

      if (bet_id) {
        showLoading("Finalizing Flip");
        try {
          await patch("/user/coinflip", {
            body: JSON.stringify({
              bet_id: bet_id.toString(),
              side: this.coin_side,
              amount: this.bet_amount,
            }),
          });
          result = Math.random() > 0.5 ? "heads" : "tails";
        } catch (e) {
          console.log(e);
          toast(`Bet ${bet_id.toString()} failed`, {
            description: "Server error occured, please notify admin with your bet id",
            type: "error",
          });
        }
      } else {
        toast("Bet failed", { description: "Couldn't get bet id from transaction", type: "error" });
      }
      hideLoading();
    } catch (e) {
      console.log(e);
      if (e.code && e.code == 4001) {
        toast("User rejected transaction", { type: "error" });
      } else {
        toast("Not enough funds", {
          description: "Please check if you have enough funds",
          type: "error",
        });
      }

      clearInterval(animation_interval);
      this.#enableButtons();
      this.#resetButtons();
      this.coinTargets.forEach((coin) => (coin.hidden = true));
      this.coinTargets[0].hidden = false;
      this.is_flipping = false;
    }
  }

  #disableButtons() {
    this.buttons.forEach((button) => (button.disabled = true));
  }

  #enableButtons() {
    this.buttons.forEach((button) => (button.disabled = false));
  }

  #resetButtons() {
    this.buttons.forEach((button) => button.removeAttribute("data-active"));
  }

  async #getFlipContract() {
    const abi = [
      {
        inputs: [{ internalType: "uint256", name: "winBP", type: "uint256" }],
        name: "bet",
        outputs: [],
        stateMutability: "payable",
        type: "function",
      },
      {
        anonymous: false,
        inputs: [
          { indexed: false, internalType: "uint256", name: "betId", type: "uint256" },
          { indexed: true, internalType: "address", name: "owner", type: "address" },
          { indexed: false, internalType: "uint256", name: "amount", type: "uint256" },
        ],
        name: "Bet",
        type: "event",
      },
    ];

    return new ethers.Contract(Current.address.flip, abi, this.signer);
  }

  async #addSomniaNetwork() {
    showLoading("Switching Network");

    const network = {
      chainId: "0x13a7",
      chainName: "Somnia",
      nativeCurrency: { name: "SOMI", symbol: "SOMI", decimals: 18 },
      rpcUrls: ["https://api.infra.mainnet.somnia.network"],
      blockExplorerUrls: ["https://explorer.somnia.network/"],
    };

    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: network.chainId }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [network],
          });
          hideLoading();
        } catch (addError) {
          toast("User rejected the request to add network", { type: "error" });
          this.#addSomniaNetwork();
        }
      } else {
        toast("Failed to switch network", { type: "error" });
      }
    }
  }
}
